import { useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
	useCancelScrapeRequestMutation,
	useGetScrapeRequestsQuery,
	useRequestRescrapeMutation,
} from "@/pages/Private/redux/customEvent/customEvent.api";
import { scrapeRequestsSelector } from "@/pages/Private/redux/customEvent/customEvent.slice";
import { useGetWebsiteScrapeCreditsQuery } from "@/pages/Private/redux/websiteScrape/websiteScrape.api";

import { useAppSelector } from "@/redux/hooks";

import { showGlobalToast } from "@/utils/showGlobalToast";

import { CustomEvent } from "@/pages/Private/pages/CustomEvent/schema/customEvent";
import { ScrapeRequestStatus } from "@/pages/Private/pages/CustomEvent/schema/scrapeRequest";

import { List } from "../../../schema/list";
import { hasOpenScrapeRequest } from "../../../components/DownloadSource/scrapedFiles";
import { scrapeRequestCancelToast } from "./scrapeRequestCancelToast";

/**
 * How long a re-scrape notice stays up. Notices go through the global toast, not a snackbar held in
 * this hook: ordering or cancelling invalidates the list, and the Sources step swaps itself for its
 * loading overlay while the list refetches, which unmounted a local snackbar a few milliseconds
 * after it opened, so nobody ever read it (PREC-4456 QA).
 */
const NOTICE_MS = 4000;

export interface UseRescrapeOptions {
	customEvent?: CustomEvent;
	list: List;
}

/**
 * Owns ordering another scrape of a source (PREC-3091): the deliveries of the source, whether a
 * new one may be ordered, and the confirm/purchase flow behind the button.
 */
export const useRescrape = ({ customEvent, list }: UseRescrapeOptions) => {
	const { t } = useTranslation();

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [showBuyCredits, setShowBuyCredits] = useState<boolean>(false);

	// Keeps the balance the confirm dialog shows in sync with the credits the API will check.
	useGetWebsiteScrapeCreditsQuery();
	useGetScrapeRequestsQuery(customEvent?.id ?? 0, { skip: !customEvent?.id });

	const [requestRescrape, { isLoading }] = useRequestRescrapeMutation();
	const [cancelScrapeRequest, { isLoading: isCancelling }] = useCancelScrapeRequestMutation();

	const scrapeRequests = useAppSelector(scrapeRequestsSelector(customEvent?.id));

	// The list payload carries the deliveries too, so the table is populated before the dedicated
	// query resolves (and stays correct if it never runs).
	const requests = useMemo(
		() => (scrapeRequests.length ? scrapeRequests : customEvent?.scrapeRequests || []),
		[scrapeRequests, customEvent?.scrapeRequests]
	);

	const openRequest = requests.find((request) => request.status === ScrapeRequestStatus.REQUESTED);

	const canRequestRescrape = !!customEvent?.id && !hasOpenScrapeRequest(requests);

	const openModal = useCallback(() => setIsModalOpen(true), []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);

	const handleConfirm = useCallback(async () => {
		if (!customEvent?.id) {
			return;
		}

		try {
			await requestRescrape({ customEventId: customEvent.id, listId: list.id }).unwrap();
			setIsModalOpen(false);
			showGlobalToast(t("customEvent.rescrape.toast.requested"), NOTICE_MS, "success");
		} catch (err) {
			console.error(err);
			showGlobalToast(t("customEvent.rescrape.toast.failed"), NOTICE_MS);
		}
	}, [customEvent?.id, list.id, requestRescrape, t]);

	/** Withdraws the open request and says what the API gave back for it (PREC-4456). */
	const handleCancelRescrape = useCallback(async () => {
		if (!openRequest) {
			return;
		}

		try {
			const cancelled = await cancelScrapeRequest({
				requestId: openRequest.id,
				listId: list.id,
			}).unwrap();
			const toast = scrapeRequestCancelToast(cancelled);

			showGlobalToast(
				toast.options ? t(toast.key, toast.options) : t(toast.key),
				NOTICE_MS,
				"success"
			);
		} catch (err) {
			console.error(err);
			showGlobalToast(t("customEvent.rescrape.toast.failed"), NOTICE_MS);
		}
	}, [openRequest, list.id, cancelScrapeRequest, t]);

	const handleBuyCredits = useCallback(() => {
		setIsModalOpen(false);
		setShowBuyCredits(true);
	}, []);

	return {
		scrapeRequests: requests,
		canRequestRescrape,
		isRescrapePending: !!openRequest,
		isCancelling,
		handleCancelRescrape,
		isModalOpen,
		isLoading,
		showBuyCredits,
		setShowBuyCredits,
		openModal,
		closeModal,
		handleConfirm,
		handleBuyCredits,
	};
};
